import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { RootStateTypeExtra } from '@store/types'

import {
  PannellumDataInterface,
  PanoramaType,
} from '@components/showcase-pannellum/types'

import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { ProjectType } from '@adUtilities/constants/common'

import GalleryTab from './GalleryTab'

interface PropertiesFooterButtonsProps {
  activeTab: string
  panoramic: PannellumDataInterface[]
  isVideoPlaying: boolean
  shouldShowGalleryTab: boolean
  hasGeneralExternalLink: boolean
  onTabChange: (arg: string) => void
  onOpenGeneralExternalLinkFrame: () => void
  blockKeys?: string[]
}

function PropertiesFooterButtons({
  activeTab,
  panoramic,
  blockKeys,
  isVideoPlaying,
  shouldShowGalleryTab,
  hasGeneralExternalLink,
  onTabChange,
  onOpenGeneralExternalLinkFrame,
}: PropertiesFooterButtonsProps) {
  const {
    sessionKey,
    projectIdentity,
    projectType,
    activeBlock,
    activeLevel,
    activePrecinctId,
    activeStageId,
    precinctList,
    panoramicButtonTitle,
  } = useSelector((state: RootStateTypeExtra) => ({
    projectIdentity: state.projectIdentity,
    sessionKey: state.projectIdentity.sessionId,
    projectType: state.projectIdentity.type,
    activeBlock: state.firestore?.session?.building?.activeBlock || '',
    activeLevel: state.firestore?.session?.building?.activeLevel || '',
    activePrecinctId:
      state.firestore?.session?.houseAndLand?.activePrecinctId || '',
    activeStageId: state.firestore?.session?.houseAndLand?.activeStageId || '',
    precinctList: state.houseAndLand.precinctList,
    panoramicButtonTitle: mapWhiteLabelsWithDefaults(state.whiteLabel).showcase
      .floorStageView.panoramicButtonTitle,
  }))

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey,
  })

  const hasPanoramicData = useMemo(() => {
    let key: string
    let type: PanoramaType

    if (projectType === ProjectType.HouseAndLand) {
      key =
        precinctList.length > 1
          ? `${activePrecinctId}-${activeStageId}`
          : activeStageId
      type = PanoramaType.Stage
    } else {
      key =
        (blockKeys?.length || 0) > 1
          ? `${activeBlock}-${activeLevel}`
          : activeLevel
      type = PanoramaType.Level
    }

    return panoramic.some(
      (item) =>
        item.type === type && (item.panoramaGroup || '').toString() === key
    )
  }, [panoramic, activeBlock, activeLevel, activePrecinctId, activeStageId])

  const onNavigateToPanoramic = async () => {
    await firebaseControlQuery.update({
      [`panoramic.scene`]: '',
    })
    await firebaseControlQuery.updateRoute('panoramic')
  }

  const showFooter =
    (shouldShowGalleryTab || hasPanoramicData || hasGeneralExternalLink) &&
    !isVideoPlaying

  if (!showFooter) {
    return null
  }

  return (
    <div className="absolute bottom-5 left-5 z-3">
      <div className="flex items-center">
        {shouldShowGalleryTab && activeTab === 'floor' && (
          <GalleryTab activeTab={activeTab} onTabChange={onTabChange} />
        )}

        {hasPanoramicData && activeTab === 'floor' && (
          <button
            type="button"
            onClick={onNavigateToPanoramic}
            className="text-md ml-2 rounded-md border border-mainColour bg-white px-8 py-2 font-medium tracking-widest text-mainColour"
          >
            {panoramicButtonTitle}
          </button>
        )}

        {hasGeneralExternalLink && activeTab === 'floor' && (
          <button
            type="button"
            onClick={onOpenGeneralExternalLinkFrame}
            className="text-md ml-2 rounded-md border border-mainColour bg-white px-8 py-2 font-medium tracking-widest text-mainColour"
          >
            Load External Link
          </button>
        )}
      </div>
    </div>
  )
}

export default PropertiesFooterButtons
